import "./styles/reset.scss";
import "./styles/global.scss";

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import ProtectedRoute from "./components/ProtectedRoute";
import { useIsAuth } from "./hooks/useIsAuth.tsx";
import { CartProvider } from "./context/CartContext";
import ErrorBoundary from "./components/ErrorBoundary";

const MainPage = lazy(() => import('./pages/MainPage'));
const AdminAuth = lazy(() => import('./pages/AdminAuth'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminPanelBanners = lazy(() => import('./pages/AdminPanelBanners'));
const AdminPanelFilters = lazy(() => import('./pages/AdminPanelFilters'));
const AdminPanelOrders = lazy(() => import('./pages/AdminPanelOrders'));
const TelegramWeb = lazy(() => import('./pages/TelegramWeb.jsx'));
const TelegramWebProduct = lazy(() => import('./pages/TelegramWebProduct.jsx'));
const TelegramNotFound = lazy(() => import('./pages/TelegramNotFound'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const CartPanel = lazy(() => import('./components/Cart'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const PublicOffer = lazy(() => import('./pages/legal/PublicOffer'));
const ConsentPd = lazy(() => import('./pages/legal/ConsentPd'));
const ConsentMailing = lazy(() => import('./pages/legal/ConsentMailing'));
const CookieBanner = lazy(() => import('./components/CookieBanner'));
const Accessories = lazy(() => import('./pages/Accessories'));

const CartPanelGuard = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/tg-app")) return null;
  return (
    <Suspense fallback={null}>
      <CartPanel />
    </Suspense>
  );
};

const RouteFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a0b0d',
    color: '#a4a4a4',
    fontFamily: 'system-ui, sans-serif',
    fontSize: 14
  }}>
    Загрузка…
  </div>
);

const App = () => {
  const { isAuth, isLoading } = useIsAuth();
  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <ErrorBoundary>
    <CartProvider>
      <ToastContainer />
      <Router>
        <CartPanelGuard />
        <Suspense fallback={null}>
          <CookieBanner />
        </Suspense>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route exact path="/" element={<MainPage/>}/>
            <Route exact path="/accessories" element={<Accessories/>}/>
            <Route exact path="/checkout" element={<Checkout/>}/>
            <Route exact path="/order/:orderNumber" element={<OrderConfirmation/>}/>
            <Route exact path="/privacy" element={<PrivacyPolicy/>}/>
            <Route exact path="/offer" element={<PublicOffer/>}/>
            <Route exact path="/consent-pd" element={<ConsentPd/>}/>
            <Route exact path="/consent-mailing" element={<ConsentMailing/>}/>
            <Route exact path="/admin" element={<AdminAuth/>}/>
            <Route exact path="/tg-app" element={<TelegramWeb /> } />
            <Route exact path="/tg-app/product/:id" element={<TelegramWebProduct />} />
            <Route path="/tg-app/*" element={<TelegramNotFound />} />
            <Route exact path="/admin-panel" element={<ProtectedRoute isAuth={isAuth} Component={AdminPanel} />}/>
            <Route exact path="/admin-panel/banners"  element={<ProtectedRoute isAuth={isAuth} Component={AdminPanelBanners} />}/>
            <Route exact path="/admin-panel/filters"  element={<ProtectedRoute isAuth={isAuth} Component={AdminPanelFilters} />}/>
            <Route exact path="/admin-panel/orders"  element={<ProtectedRoute isAuth={isAuth} Component={AdminPanelOrders} />}/>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
    </CartProvider>
    </ErrorBoundary>
  );
};

export default App;
